import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { UserIcon, Cog6ToothIcon } from "@heroicons/react/24/outline";
import { SignOutButton } from "./SignOutButton";


const ProfileDropdown = ({ user}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const name = sessionStorage.getItem("entra_name") || "User";
  const email = sessionStorage.getItem("entra_email") || "user@example.com";

  // Calculate initials from name (first and last letter)
  const getInitials = (fullName: string): string => {
    if (!fullName || fullName.trim().length === 0) return "U";
    
    const trimmedName = fullName.trim();
    const nameParts = trimmedName.split(/\s+/).filter(part => part.length > 0);
    
    if (nameParts.length === 0) return "U";
    
    if (nameParts.length === 1) {
      // Single name - use first and last letter of that name
      const singleName = nameParts[0];
      if (singleName.length === 1) return singleName.toUpperCase();
      return (singleName[0] + singleName[singleName.length - 1]).toUpperCase();
    }
    
    // Multiple names - use first letter of first name and first letter of last name
    const firstName = nameParts[0];
    const lastName = nameParts[nameParts.length - 1];
    return (firstName[0] + lastName[0]).toUpperCase();
  };

  const initials = getInitials(name);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !(ref.current as HTMLDivElement).contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={ref}>
      {/* Avatar and name */}
      <button
        className="flex items-center gap-3 focus:outline-none group hover:opacity-90"
        onClick={() => setOpen((o) => !o)}
      >
        <div className="flex flex-col items-end">
          <span className="font-semibold text-gray-900 group-hover:text-[#32196c]">{name}</span>
        </div>
        <div className="bg-[#32196c] text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg shadow-sm">
          {initials}
        </div>
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 text-sm mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50 animate-fadeInUp overflow-hidden">
          <div className="p-4 border-b border-gray-100 bg-gray-50">
            <div className="font-bold text-gray-900">{name}</div>
            <div className="text-sm text-gray-600 mt-0.5">{email}</div>
          </div>
          <div className="p-2">
            <button 
              onClick={() => {
                setOpen(false);
                navigate("/profile");
              }}
              className="flex items-center gap-3 w-full px-4 py-3 hover:bg-gray-100 text-left rounded-lg group"
            >
              <UserIcon className="w-5 h-5 text-gray-600 group-hover:text-[#32196c]" />
              <span className="font-medium text-gray-700 group-hover:text-[#32196c]">Profile</span>
            </button>
            <button 
              onClick={() => {
                setOpen(false);
                navigate("/settings");
              }}
              className="flex items-center gap-3 w-full px-4 py-3 hover:bg-gray-100 text-left rounded-lg group"
            >
              <Cog6ToothIcon className="w-5 h-5 text-gray-600 group-hover:text-[#32196c]" />
              <span className="font-medium text-gray-700 group-hover:text-[#32196c]">Settings</span>
            </button>
            <div className="border-t border-gray-100 my-1" />
            <SignOutButton />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileDropdown; 